import { useEffect, useRef } from 'react'
import DsButton from './Button'
import Eyebrow from './Eyebrow'
import { CheckIcon } from './icons'
import { SettingsSaveState } from './SettingsSaveContext'

export interface SettingsFooterTab {
  id: string,
  label: string,
  count?: number,
  danger?: boolean,
}

/**
 * Everything that lives below the content on a Settings screen.
 *
 * The save bar, when there is something to save, and the section menu, when it
 * has been opened. Both sit above the mobile tab bar; on desktop the menu is
 * gone (the tab row is on screen) and the bar returns to the bottom of the
 * window.
 *
 * The menu has no trigger of its own — the Settings tab in the bar below is the
 * trigger. A strip carrying one sat directly above that bar repeating the word
 * it already said, and cost 56px of a phone to do it.
 */
const SettingsFooter = ({ saveState, tabs, activeTabId, onSelectTab, isOpen, onClose }: {
  saveState: SettingsSaveState | null,
  tabs: SettingsFooterTab[],
  activeTabId: string,
  /** The caller decides whether the move is allowed — it is what knows there
   * are unsaved changes to warn about. */
  onSelectTab: (id: string) => void,
  isOpen: boolean,
  onClose: () => void,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Element | null;
      // The Settings tab is the trigger and toggles on its own; closing here
      // too would shut the menu and reopen it in the same gesture.
      if (target?.closest?.('[data-settings-menu-trigger]')) {
        return;
      }
      if (ref.current && !ref.current.contains(target as Node)) {
        onClose();
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);

  const isDirty = !!saveState && saveState.dirtyCount > 0;
  // A bar can stay up with nothing to save — the Plan tab, where it is where
  // the price lives — and then only its buttons change.
  const hasSaveBar = isDirty || !!saveState?.alwaysVisible;

  /**
   * The support launcher parks in the bottom-right corner, which is exactly
   * where the save bar puts Save — it sat on top of the button. On a phone the
   * launcher is already hidden on these screens, so this only has to move it on
   * desktop, and only while the bar is up.
   */
  const hasTallSaveBar = hasSaveBar && !!saveState?.total;

  useEffect(() => {
    document.body.classList.toggle('b4a2-save-bar', hasSaveBar);
    // The bar carrying a total is taller, and the launcher has to clear it too.
    document.body.classList.toggle('b4a2-save-bar-tall', hasTallSaveBar);
    return () => {
      document.body.classList.remove('b4a2-save-bar');
      document.body.classList.remove('b4a2-save-bar-tall');
    };
  }, [hasSaveBar, hasTallSaveBar]);

  // A bar that leads to a price (the Plan tab) carries the total beside the
  // buttons and a size up: the click is a purchase, and the number it buys has
  // to be read before it, not after.
  const hasTotal = !!saveState?.total;

  const saveBar = hasSaveBar && saveState ? (
    <div className={`${hasTotal ? 'min-h-[84px] py-3' : 'h-[62px] short:h-[52px]'} px-4 lg:px-8 bg-navy-800 border-t border-navy-500 flex items-center justify-between gap-3`}>
      <div className="min-w-0 flex flex-col gap-0.5">
        <span className={`min-w-0 truncate ${hasTotal ? 'text-ds-15 text-white' : 'text-ds-135 text-navy-200'}`}>
          {saveState.summary
            ?? `${saveState.dirtyCount} unsaved change${saveState.dirtyCount === 1 ? '' : 's'} in this section`}
        </span>
        {!isDirty && saveState.hint ? (
          <button
            type="button"
            onClick={saveState.onHint}
            className="b4-focus self-start truncate max-w-full rounded-control text-left text-ds-125 text-action-soft hover:text-white transition-colors duration-[120ms]"
          >
            {saveState.hint}
          </button>
        ) : null}
      </div>
      <div className={`flex items-center flex-none ${hasTotal ? 'gap-4 lg:gap-6' : 'gap-2'}`}>
        {hasTotal ? (
          <div className="hidden sm:flex flex-col items-end gap-0.5">
            <Eyebrow>
              {isDirty ? saveState.totalLabel ?? 'new total' : saveState.idleTotalLabel ?? 'total'}
            </Eyebrow>
            <span className="whitespace-nowrap">
              <span className="font-sora text-[28px] leading-[1.1] font-semibold tracking-[-0.02em] text-white">
                {saveState.total}
              </span>
              <span className="text-ds-15 text-navy-300"> {saveState.totalUnit ?? '/ month'}</span>
            </span>
          </div>
        ) : null}
        <div className="flex items-center gap-2">
          {isDirty ? (
            <DsButton
              variant="outline"
              size={hasTotal ? 'lg' : undefined}
              onClick={saveState.onDiscard}
              isDisabled={saveState.isSubmitting}
            >
              Discard
            </DsButton>
          ) : null}
          <DsButton
            variant="primary"
            size={hasTotal ? 'lg' : undefined}
            onClick={saveState.onSave}
            isDisabled={!isDirty || saveState.isSubmitting || !saveState.canSave}
          >
            {!isDirty
              ? saveState.idleLabel ?? 'No changes yet'
              : saveState.isSubmitting
              ? saveState.savingLabel ?? 'Saving…'
              : saveState.saveLabel ?? 'Save changes'}
          </DsButton>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div
      ref={ref}
      className="fixed z-30 left-0 right-0 lg:left-[300px] bottom-[calc(75px_+_env(safe-area-inset-bottom))] short:bottom-[calc(45px_+_env(safe-area-inset-bottom))] lg:bottom-0 flex flex-col"
    >
      {/* The panel, above both strips. It is a list rather than a native
          <select> so the counts and the danger tone survive. */}
      {isOpen ? (
        <div className="lg:hidden mx-3 mb-2 rounded-container border border-navy-500 bg-navy-800 shadow-[0_8px_32px_rgba(0,0,0,.5)] overflow-y-auto b4-thin max-h-[min(70vh,26rem,calc(100vh_-_120px))]">
          <div className="sticky top-0 z-10 bg-navy-800 px-4 pt-3.5 pb-2 short:pt-2 short:pb-1.5"><Eyebrow>settings</Eyebrow></div>
          {/* Sideways it goes two-up. A phone on its side is short and wide, and
              six rows in one column asked the short axis for everything while
              leaving half the wide one empty — the list scrolled for no reason.
              The 1px grid gaps over a navy ground draw the rules, so the same
              markup rules correctly in one column or two. */}
          <div className="flex flex-col short:grid short:grid-cols-2 gap-px bg-navy-700">
            {tabs.map(tab => {
              const isActive = tab.id === activeTabId;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => { onClose(); onSelectTab(tab.id); }}
                  className={[
                    'b4-focus w-full min-h-[48px] short:min-h-[38px] px-4 flex items-center gap-3 text-left text-ds-135',
                    'transition-colors duration-[120ms]',
                    isActive
                      ? 'bg-navy-600 text-white font-semibold'
                      : `bg-navy-800 ${tab.danger ? 'text-state-err' : 'text-navy-100'}`,
                  ].join(' ')}
                >
                  <span className="flex-1 min-w-0 truncate">{tab.label}</span>
                  {tab.count ? (
                    <span className="flex-none font-courrier text-ds-115 text-navy-300">{tab.count}</span>
                  ) : null}
                  {isActive ? <CheckIcon size={16} className="flex-none" /> : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {saveBar}
    </div>
  );
}

export default SettingsFooter
